import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Divider,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Email,
  Support,
  Assignment,
  Storage,
  Security
} from '@mui/icons-material';
import { useAdminLanguage } from './AdminI18n';
import { AdminPageContext } from '../Context';

function Home() {
  const { t } = useAdminLanguage();
  const { setAdminPage } = useContext(AdminPageContext);
  const [stats, setStats] = useState({
    users: 0,
    reports: 0,
    rubrics: 0,
    magistrats: 0,
    hospitals: 0,
    garages: 0,
    places: 0,
    standards: 0,
    loading: true
  });
  const [licenseInfo, setLicenseInfo] = useState({
    isActive: false,
    daysRemaining: null,
    pocketAccess: null,
    loading: true
  });
  const [zonalData, setZonalData] = useState({
    clientName: '',
    zoneName: '',
    version: '',
    loading: true
  });
  const [dataRetention, setDataRetention] = useState({
    reports: {
      autoDelete: false,
      retentionDays: 365
    },
    logs: {
      autoDelete: false,
      retentionDays: 90
    },
    loading: true
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Récupération du token d'authentification
        const token = sessionStorage.getItem('authToken');
        const authHeaders = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
        
        // Récupération des statistiques depuis les différentes API
        const [usersRes, reportsRes, rubricsRes, magistratsRes, hospitalsRes, garagesRes, placesRes, standardsRes] = await Promise.all([
          fetch('/api/user', authHeaders).catch(() => ({json: () => []})),
          fetch('/api/report', authHeaders).catch(() => ({json: () => []})),
          fetch('/api/rubrics').catch(() => ({json: () => []})),
          fetch('/api/magistrats').catch(() => ({json: () => []})),
          fetch('/api/hospitals').catch(() => ({json: () => []})),
          fetch('/api/garages').catch(() => ({json: () => []})),
          fetch('/api/places').catch(() => ({json: () => []})),
          fetch('/api/standards').catch(() => ({json: () => []}))
        ]);

        const [users, reports, rubrics, magistrats, hospitals, garages, places, standards] = await Promise.all([
          usersRes.json?.() || [],
          reportsRes.json?.() || [],
          rubricsRes.json?.() || [],
          magistratsRes.json?.() || [],
          hospitalsRes.json?.() || [],
          garagesRes.json?.() || [],
          placesRes.json?.() || [],
          standardsRes.json?.() || []
        ]);

        setStats({
          users: users.length || 0,
          reports: reports.length || 0,
          rubrics: rubrics.length || 0,
          magistrats: magistrats.length || 0,
          hospitals: hospitals.length || 0,
          garages: garages.length || 0,
          places: places.length || 0,
          standards: standards.length || 0,
          loading: false
        });
      } catch (error) {
        console.error('Erreur lors du chargement des statistiques:', error);
        setStats(prev => ({ ...prev, loading: false }));
      }
    };

    const fetchLicenseInfo = async () => {
      try {
        const token = sessionStorage.getItem('authToken');
        const response = await fetch('/api/license', {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        const data = await response.json();
        
        if (data.license) {
          const expDate = new Date(data.license.expirationDate || data.license.expiration);
          const today = new Date();
          const days = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
          
          setLicenseInfo({
            isActive: data.valid === true && days > 0,
            daysRemaining: days,
            pocketAccess: data.license.pocketAccess === true,
            loading: false
          });
        } else {
          setLicenseInfo({ isActive: false, daysRemaining: null, pocketAccess: null, loading: false });
        }
      } catch (error) {
        console.error('Erreur lors du chargement de la licence:', error);
        setLicenseInfo({ isActive: false, daysRemaining: null, pocketAccess: null, loading: false });
      }
    };

    const fetchZonalData = async () => {
      try {
        const response = await fetch('/api/settings');
        const data = await response.json();
        setZonalData({
          clientName: data.clientName || '',
          clientNumber: data.clientNumber || '',
          version: data.version || '',
          loading: false
        });
      } catch (error) {
        console.error('Erreur lors du chargement des données zonales:', error);
        setZonalData({ clientName: '', zoneName: '', version: '', loading: false });
      }
    };

    const fetchDataRetention = async () => {
      try {
        const response = await fetch('/api/dataretention');
        const data = await response.json();
        setDataRetention({
          reports: data.reports || { autoDelete: false, retentionDays: 365 },
          logs: data.logs || { autoDelete: false, retentionDays: 90 },
          loading: false
        });
      } catch (error) {
        console.error('Erreur lors du chargement des paramètres de rétention:', error);
        setDataRetention(prev => ({ ...prev, loading: false }));
      }
    };

    fetchStats();
    fetchLicenseInfo();
    fetchZonalData();
    fetchDataRetention();
  }, []);

  const getLicenseChip = () => {
    if (licenseInfo.loading) return <CircularProgress size={20} />;
    
    if (!licenseInfo.isActive) {
      return <Chip label={t('Licence expirée')} color="error" size="small" />;
    } else if (licenseInfo.daysRemaining <= 7) {
      return <Chip label={t('Expire dans {days} jours', { days: licenseInfo.daysRemaining })} color="warning" size="small" />;
    } else {
      return <Chip label={t('Licence active')} color="success" size="small" />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* En-tête principal */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
          {t("Espace d'administration de BlueNote")}
        </Typography>
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
          {zonalData.loading ? t('Chargement...') : `${zonalData.clientName} ( ${zonalData.clientNumber} )`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
          {getLicenseChip()}
          {!zonalData.loading && (
            <Chip label={`${t('Version')} ${zonalData.version}`} variant="outlined" size="small" />
          )}
        </Box>
        <Divider />
      </Box>

      {/* Statistiques principales */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Assignment color="primary" sx={{ mr: 1 }} />
                {t("Vue d'ensemble du système")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Box 
                    onClick={() => setAdminPage('UserAdmin')}
                    sx={{ 
                      textAlign: 'center', 
                      p: 2, 
                      cursor: 'pointer',
                      borderRadius: 1,
                      transition: 'background-color 0.2s',
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <Typography variant="h4" color="primary.main" sx={{ fontWeight: 'bold' }}>
                      {stats.loading ? <CircularProgress size={24} /> : stats.users}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('Utilisateurs')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box 
                    onClick={() => setAdminPage('ReportAdmin')}
                    sx={{ 
                      textAlign: 'center', 
                      p: 2, 
                      cursor: 'pointer',
                      borderRadius: 1,
                      transition: 'background-color 0.2s',
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <Typography variant="h4" color="primary.main" sx={{ fontWeight: 'bold' }}>
                      {stats.loading ? <CircularProgress size={24} /> : stats.reports}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('Documents')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box 
                    onClick={() => setAdminPage('RubricAdmin')}
                    sx={{ 
                      textAlign: 'center', 
                      p: 2, 
                      cursor: 'pointer',
                      borderRadius: 1,
                      transition: 'background-color 0.2s',
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <Typography variant="h4" color="primary.main" sx={{ fontWeight: 'bold' }}>
                      {stats.loading ? <CircularProgress size={24} /> : stats.rubrics}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('Rubriques')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box 
                    onClick={() => setAdminPage('StandardAdmin')}
                    sx={{ 
                      textAlign: 'center', 
                      p: 2, 
                      cursor: 'pointer',
                      borderRadius: 1,
                      transition: 'background-color 0.2s',
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <Typography variant="h4" color="primary.main" sx={{ fontWeight: 'bold' }}>
                      {stats.loading ? <CircularProgress size={24} /> : stats.standards}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('PV canevas')}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card 
            elevation={3}
            onClick={() => setAdminPage('LicenseAdmin')}
            sx={{ 
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { 
                transform: 'translateY(-2px)',
                boxShadow: 6
              }
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Security color="primary" sx={{ mr: 1 }} />
                {t('État de la licence')}
              </Typography>
              {licenseInfo.loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={30} />
                </Box>
              ) : (
                <Box>
                  {licenseInfo.isActive ? (
                    <Box>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Statut :</strong> {t('Licence active')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Jours restants : {licenseInfo.daysRemaining}
                      </Typography>
                      {licenseInfo.daysRemaining <= 30 && (
                        <Typography variant="body2" color="warning.main" sx={{ fontWeight: 'bold' }}>
                          {t('Renouvellement recommandé')}
                        </Typography>
                      )}
                      {licenseInfo.pocketAccess !== null && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {t('Accès BlueNote Pocket')} : <strong>{licenseInfo.pocketAccess ? t('Activé') : t('Désactivé')}</strong>
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="body1" color="error.main" sx={{ mb: 1, fontWeight: 'bold' }}>
                        {t('Licence expirée')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {t('Contactez le support pour renouveler')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                      contact@pyll.be
                      </Typography>
                      {licenseInfo.pocketAccess !== null && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {t('Accès BlueNote Pocket')} : <strong>{licenseInfo.pocketAccess ? t('Activé') : t('Désactivé')}</strong>
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Deuxième ligne : Support + Gestion des données */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Support color="primary" sx={{ mr: 1 }} />
                {t("Besoin d'aide ?")}
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {t("Notre équipe support est disponible pour vous accompagner dans l'utilisation de BlueNote.")}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {t("Que ce soit pour des questions techniques, des demandes de nouvelles fonctionnalités ou le renouvellement de votre licence, nous sommes là pour vous aider.")}
                  </Typography>
                  <Typography variant="body1">
                    {t("Contactez-nous à l'adresse suivante en mentionnant votre version de BlueNote :")} {`v ${zonalData.version} - ${zonalData.clientNumber} `}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Email sx={{ mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      contact@pyll.be
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card 
            elevation={3}
            onClick={() => setAdminPage('DataAdmin')}
            sx={{ 
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { 
                transform: 'translateY(-2px)',
                boxShadow: 6
              }
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Storage color="primary" sx={{ mr: 1 }} />
                {t('Gestion des données')}
              </Typography>
              {dataRetention.loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={30} />
                </Box>
              ) : (
                <Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {t('Procès-verbaux')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {dataRetention.reports.autoDelete ? 
                        t('Suppression auto après {days} jours', { days: dataRetention.reports.retentionDays }) :
                        t('Conservation indéfinie')
                      }
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {t('Journalisation')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {dataRetention.logs.autoDelete ? 
                        t('Suppression auto après {days} jours', { days: dataRetention.logs.retentionDays }) :
                        t('Conservation indéfinie')
                      }
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Home;